import React, { FC, useRef, useEffect, useContext, useMemo } from 'react'
import store from 'store2'
import classNames from 'classnames'
import Buttons from './Buttons'
import { ChatContext } from '~/service/chatConn'
import MainInput from './MainInput'
import { debounce } from 'lodash'
import Autocomplete from './Autocomplete'
import Additional from './Additional'
import { Controller, UseFormSetValue, useForm } from 'react-hook-form'
import Dropzone from './Dropzone'
import { Attachment, uploadUseDropzone } from '~/components/common/upload'
import Attachments from './Attachments'

type ChatInputProps = {
  scrollAtEnd: boolean
}

export type FormType = {
  content: string
  redirectToUser?: number
  redirectToStory?: number
  attachments: Attachment[]
  aiLang: string
}

const defaultValues: FormType = {
  content: '',
  redirectToUser: undefined,
  redirectToStory: undefined,
  attachments: [],
  aiLang: 'en',
}

let ChatInput: FC<ChatInputProps> = ({ scrollAtEnd }) => {
  const {
    sendMessage,
    sendMessageAndRedirectToOperator,
    redirectToStory: redirectToStoryAction,
    activeChat,
    sendTyping,
  } = useContext(ChatContext)
  const inputRef = useRef<HTMLTextAreaElement>()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const typingRef = useRef(false)

  const autocompleteActiveRef = useRef<string>()

  const onEndTyping = () => {
    if (typingRef.current) {
      typingRef.current = false

      sendTyping(activeChat.id, false)
    }
  }

  const typingDebounce = useMemo(() => debounce(onEndTyping, 5000), [])

  useEffect(() => onEndTyping, [])

  const storeKey = activeChat.id + 'form'

  const initialValues = useMemo(
    () => store(storeKey) || defaultValues,
    []
  ) as FormType

  const { handleSubmit, getValues, watch, reset, setValue, control } =
    useForm<FormType>({
      defaultValues: initialValues,
      mode: 'onChange',
    })

  const { getRootProps, getInputProps, isDragActive, isDraggingOverWindow } =
    upload(setValue, getValues, inputRef)

  useEffect(() => {
    inputRef.current?.focus()
  }, [isDragActive, isDraggingOverWindow])

  useEffect(() => {
    const values = getValues()

    const valuesWithoutUnsentAttachments = {
      ...values,
      attachments: values.attachments.filter(a => !a.uploading),
    }

    store(storeKey, valuesWithoutUnsentAttachments)

    typingDebounce()

    if (!typingRef.current && values.content.length) {
      typingRef.current = true

      sendTyping(activeChat.id, true)
    }
  }, [watch('content'), watch('redirectToStory'), watch('attachments')])

  // aby ten komponent się zre-renderował gdy kasujemy redirectToStory
  watch('redirectToStory')
  watch('redirectToUser')
  watch('attachments')
  const aiLang = watch('aiLang')

  const { redirectToStory, redirectToUser, content, attachments } = getValues()
  const anyUploading = attachments.some(a => a.uploading)
  const anyContent = content.length > 0 && content != '/'
  const disabledSubmit =
    !(anyContent || redirectToStory || redirectToUser) ||
    !!activeChat.aiCompletion ||
    anyUploading

  const submit = handleSubmit(
    async ({ content, redirectToUser, redirectToStory, attachments }) => {
      if (disabledSubmit) {
        return
      }

      if (redirectToUser) {
        await sendMessageAndRedirectToOperator(redirectToUser, content)
      } else if (redirectToStory) {
        await redirectToStoryAction(redirectToStory)
      } else {
        await sendMessage(content, attachments)
      }

      onEndTyping()

      store(storeKey, undefined)

      reset(defaultValues)
    }
  )

  return (
    <form
      onSubmit={submit}
      className={classNames('chat-input', {
        'is-scrollAtEnd': scrollAtEnd,
      })}
    >
      <Autocomplete
        inputRef={inputRef}
        content={getValues().content}
        redirectToUser={redirectToUser}
        autocompleteActiveRef={autocompleteActiveRef}
        setValue={setValue}
        wrapperRef={wrapperRef}
      />

      <div
        className={classNames('chat-input-textarea', {
          'is-dragActive': isDragActive && window.c.chatAttachments,
        })}
        ref={wrapperRef}
        onClick={() => {
          inputRef.current && inputRef.current.focus()
        }}
      >
        {isDraggingOverWindow && window.c.chatAttachments && (
          <Dropzone getRootProps={getRootProps} />
        )}

        <Attachments attachments={attachments} setValue={setValue} />

        <Additional
          setValue={setValue}
          redirectToStory={redirectToStory}
          redirectToUser={redirectToUser}
        />

        <Controller
          name="content"
          control={control}
          render={renderProps => (
            <MainInput
              renderProps={renderProps}
              redirectToUser={redirectToUser}
              redirectToStory={redirectToStory}
              inputRef={inputRef}
              autocompleteActiveRef={autocompleteActiveRef}
              hidden={!!redirectToStory}
              submit={submit}
            />
          )}
        />

        {!activeChat.stopped && (
          <Buttons
            redirectToStory={redirectToStory}
            setValue={setValue}
            inputRef={inputRef}
            disabledSubmit={disabledSubmit}
            anyContent={anyContent}
            control={control}
            getInputProps={getInputProps}
            getRootProps={getRootProps}
            aiLang={aiLang}
          />
        )}
      </div>
    </form>
  )
}

export default ChatInput

const upload = (
  setValue: UseFormSetValue<FormType>,
  getValues: () => FormType,
  inputRef: React.MutableRefObject<HTMLTextAreaElement | undefined>
) =>
  uploadUseDropzone(
    window.c.maxFileSize.livechat,
    'chat/upload',
    attachment =>
      setValue('attachments', [...getValues().attachments, attachment]),
    attachment => {
      const attachments = getValues().attachments

      setValue(
        'attachments',
        attachment.error
          ? attachments.filter(a => a.uuid !== attachment.uuid)
          : attachments.map(a => (a.uuid === attachment.uuid ? attachment : a))
      )
    },
    inputRef
  )
